import { useState, useEffect } from 'react';
import { getRedirectResult } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getClubMarks, getClubEvents } from '../lib/firebase';
import { loadFleetConfig } from '../lib/fleetConfigStore';

function FirestoreTest() {
  const [results, setResults] = useState('Click to test...');
  const run = async () => {
    setResults('Testing...');
    const lines = [];
    try {
      const marks = await getClubMarks('ie-dublin-bay');
      lines.push(`✅ marks: ${marks.length}`);
    } catch (e) { lines.push(`❌ marks: ${e.code} ${e.message}`); }
    try {
      const events = await getClubEvents('ie-dublin-bay');
      lines.push(`✅ events: ${events.length}`);
    } catch (e) { lines.push(`❌ events: ${e.code} ${e.message}`); }
    try {
      const fc = await loadFleetConfig('ie-dublin-bay');
      lines.push(`✅ fleet_config: ${JSON.stringify(fc).slice(0, 80)}`);
    } catch (e) { lines.push(`❌ fleet_config: ${e.code} ${e.message}`); }
    setResults(lines.join('\n'));
  };
  return (
    <div>
      <button onClick={run} style={{ marginBottom: '0.5rem' }}>Run Firestore test</button>
      <pre style={{ margin: 0, fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{results}</pre>
    </div>
  );
}

export default function AuthDebug() {
  const { user, portalRole, loading } = useAuth();
  const [redirectResult, setRedirectResult] = useState('checking...');
  const [tokenClaims, setTokenClaims] = useState(null);
  const [tokenError, setTokenError] = useState(null);

  useEffect(() => {
    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          setRedirectResult(`Got redirect result: user=${result.user?.email}`);
        } else {
          setRedirectResult('No redirect result (normal if not coming from Google redirect)');
        }
      })
      .catch((err) => {
        setRedirectResult(`ERROR: ${err.code} — ${err.message}`);
      });
  }, []);

  useEffect(() => {
    if (!user) return;
    user.getIdTokenResult()
      .then((r) => setTokenClaims(r.claims))
      .catch((err) => setTokenError(err.message));
  }, [user]);

  const s = {
    page: { fontFamily: 'monospace', padding: '2rem', maxWidth: '800px' },
    section: { background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', padding: '1rem', marginBottom: '1rem' },
    label: { fontWeight: 'bold', color: '#555', fontSize: '0.85rem' },
    value: { marginTop: '0.25rem', wordBreak: 'break-all' },
    ok: { color: 'green' },
    err: { color: 'red' },
    warn: { color: 'orange' },
  };

  return (
    <div style={s.page}>
      <h2>Auth Debug</h2>
      <p style={{ color: '#888', fontSize: '0.85rem' }}>Remove this page before go-live. Do not share this URL.</p>

      <div style={s.section}>
        <div style={s.label}>Auth Context State</div>
        <div style={s.value}>
          <span style={s.label}>loading: </span>
          <span style={loading ? s.warn : s.ok}>{String(loading)}</span>
        </div>
        <div style={s.value}>
          <span style={s.label}>user: </span>
          {user
            ? <span style={s.ok}>{user.email} (uid: {user.uid})</span>
            : <span style={s.err}>null</span>}
        </div>
        <div style={s.value}>
          <span style={s.label}>portalRole: </span>
          {portalRole
            ? <span style={s.ok}>{JSON.stringify(portalRole)}</span>
            : <span style={s.err}>null</span>}
        </div>
      </div>

      <div style={s.section}>
        <div style={s.label}>Redirect Result (this page load)</div>
        <div style={s.value}>{redirectResult}</div>
      </div>

      <div style={s.section}>
        <div style={s.label}>Raw ID Token Claims</div>
        {!user && <div style={s.value}><span style={s.warn}>Not signed in</span></div>}
        {tokenError && <div style={s.value}><span style={s.err}>{tokenError}</span></div>}
        {tokenClaims && (
          <pre style={{ margin: 0, fontSize: '0.85rem', overflow: 'auto' }}>
            {JSON.stringify(tokenClaims, null, 2)}
          </pre>
        )}
      </div>

      <div style={s.section}>
        <div style={s.label}>Firebase Auth Config</div>
        <div style={s.value}>
          <span style={s.label}>currentUser: </span>
          {auth.currentUser
            ? <span style={s.ok}>{auth.currentUser.email}</span>
            : <span style={s.err}>null</span>}
        </div>
        <div style={s.value}>
          <span style={s.label}>authDomain: </span>
          <span>{auth.config?.authDomain || '(check firebase.js)'}</span>
        </div>
        <div style={s.value}>
          <span style={s.label}>current URL: </span>
          <span>{window.location.href}</span>
        </div>
      </div>

      <div style={s.section}>
        <div style={s.label}>Firestore Read Test (clubs/ie-dublin-bay)</div>
        <FirestoreTest />
      </div>

      {user && (
        <button
          style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
          onClick={() => auth.signOut()}
        >
          Sign out
        </button>
      )}
    </div>
  );
}
